import axios from 'axios';
import { fileDocumentTypes } from './interfaces';

const apiURL = process.env.REACT_APP_API_URL;

export interface Employee {
    employeeId: number;
    employeeName: string;
    position: string | null;
    subcontractor: boolean;
    sortOrder: number | null;
    sageDepartment: string | null;
    adminRole: boolean;
    email: string | null;
    worktypeDefaultId: number | null;
    worktypeDefault: any | null;
    worktypegroupDefaultId: number | null;
    worktypegroupDefault: any | null;
    hourlyRate: number | null;
    mobilePhone: string | null;
    vehicleRegistration: string | null;
    permissions: any | null;
    fcmTokens: any[];
    type: string | null;
}

export interface FormQuestionType {
    formQuestionTypeId: number;
    name: string;
    description: string | null;
}

export interface FormQuestionOption {
    formQuestionOptionId?: number;
    formQuestionId?: number;
    name: string;
}

export interface FormQuestion {
    formQuestionId: number;
    formId: number;
    name: string;
    description: string | null;
    placeholder: string | null;
    templatePlaceholder?: string | null;
    section: number;
    order: number;
    formQuestionTypeId: number;
    isRequired: boolean;
    minLength: number | null;
    maxLength: number | null;
    // Decimal places allowed on a Number question; 0 means whole numbers only.
    // Set on every question but only meaningful for the Number type.
    decimalPlaces: number;
    regex: string | null;
    validationMessage: string | null;
    createdDate: string;
    employeeCreatedbyId: number;
    modifiedDate: string | null;
    employeeModifiedbyId: number | null;
    deletedDate: string | null;
    employeeDeletedbyId: number | null;
    form: any | null;
    formQuestionType: FormQuestionType;
    formQuestionOptions?: FormQuestionOption[] | null;
    employeeCreatedby: Employee;
    employeeModifiedby: Employee | null;
    employeeDeletedby: Employee | null;
}

export interface FormSection {
    formSectionId: number;
    formId: number;
    name: string;
    description: string | null;
    order: number;
    createdDate: string;
    employeeCreatedbyId: number;
    modifiedDate: string | null;
    employeeModifiedbyId: number | null;
    deletedDate: string | null;
    employeeDeletedbyId: number | null;
    showOn: string | null;
    form: any | null;
    employeeCreatedby: Employee;
    employeeModifiedby: Employee | null;
    employeeDeletedby: Employee | null;
    formQuestions: FormQuestion[];
}

export interface FormDetail {
    formId: number;
    name: string;
    description: string;
    createdDate: string;
    employeeCreatedbyId: number;
    modifiedDate: string | null;
    employeeModifiedbyId: number | null;
    deletedDate: string | null;
    employeeDeletedbyId: number | null;
    showLocationJobs: boolean;
    showLocationLinks: boolean;
    createFromTemplate?: boolean;
    templateFilename?: string;
    automationUrl?: string;
    /**
     * Short, per-form identifier. Stored only - it is there for the automation
     * that generates the form to key off.
     */
    shortCode?: string;
    /**
     * Records that a response should be raised against a job automatically.
     * Stored only - nothing acts on it yet.
     */
    autoGenerateForJob?: boolean;
    employeeCreatedby: Employee;
    employeeModifiedby: Employee | null;
    employeeDeletedby: Employee | null;
    formSections: FormSection[];
}

export interface Form {
    formId: number;
    name: string;
    description: string;
    createdDate: string;
    createdBy: string;
    modifiedDate: string | null;
    modifiedBy: string | null;
    deletedDate: string | null;
    showLocationJobs: boolean;
    showLocationLinks: boolean;
    questionCount: number;
    sectionCount: number;
    automationUrl: string;
    autoGenerateForJob?: boolean;
}

export interface DropdownOption {
    value: number;
    text: string;
}

export interface FormResponse {
    formResponseId: number;
    formId: number;
    formName: string;
    jobId: number | null;
    jobRef?: number;
    job?: { ref: number };
    /** Flattened from the job so the grid can sort and filter on them. */
    jobReference?: string | null;
    clientName?: string | null;
    siteAddress?: string | null;
    createdDate: string;
    createdBy: Employee;
    employeeCreatedbyId: number;
    employeeCreatedby?: Employee;
    formQuestionResponses?: any[];
}

export const getForms = async (): Promise<Form[]> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    console.log('Fetching forms from:', `https://${apiURL}/forms/forgrid`);

    const response = await axios.get<Form[]>(`https://${apiURL}/forms/forgrid`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });

    console.log('Forms API response:', response.data);

    return response.data;
};

export const getFormsDropdown = async (): Promise<DropdownOption[]> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.get<Form[]>(`https://${apiURL}/forms/forgrid`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });

    // Map forms to dropdown format
    return response.data
        .filter(form => !form.deletedDate) // Exclude deleted forms
        .map((form) => ({
            value: form.formId,
            text: form.name,
        }));
};

export const getFormById = async (formId: number): Promise<FormDetail> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.get<FormDetail>(`https://${apiURL}/forms/${formId}`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });

    return response.data;
};

export const getFormQuestionTypes = async (): Promise<DropdownOption[]> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.get(`https://${apiURL}/FormQuestionTypes/Dropdown`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });

    // Map the API response to DropdownOption format
    return response.data.map((item: any) => ({
        value: item.formQuestionTypeId,
        text: item.name,
    }));
};

/**
 * Copies a form - sections, questions and their options - under a new name.
 * Responses are not copied, and the copy's short code and "generate for job"
 * setting are left clear for whoever repurposes it.
 */
export const duplicateForm = async (formId: number, name: string): Promise<Form> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.post(
        `https://${apiURL}/Forms/${formId}/Duplicate`,
        { name },
        {
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
        }
    );

    return response.data;
};

export const createFormSection = async (data: any): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.post(`https://${apiURL}/FormSections`, data, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const updateFormSection = async (id: number, data: any): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.put(`https://${apiURL}/FormSections/${id}`, data, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const createFormQuestion = async (data: any): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.post(`https://${apiURL}/FormQuestions`, data, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const updateFormQuestion = async (id: number, data: any): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.put(`https://${apiURL}/FormQuestions/${id}`, data, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const deleteFormSection = async (id: number): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.delete(`https://${apiURL}/FormSections/${id}`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const deleteFormQuestion = async (id: number): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.delete(`https://${apiURL}/FormQuestions/${id}`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const updateForm = async (id: number, data: any): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.put(`https://${apiURL}/Forms/${id}`, data, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const deleteForm = async (id: number): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.delete(`https://${apiURL}/Forms/${id}`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const createForm = async (data: any): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.post(`https://${apiURL}/Forms`, data, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};

export const getFormResponses = async (formId: number): Promise<FormResponse[]> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.get(`https://${apiURL}/FormResponse/Form/${formId}`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });

    return response.data;
};

export const getFormDocuments = async (formId: number): Promise<fileDocumentTypes[]> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    const response = await axios.get(`https://${apiURL}/Forms/${formId}/Documents`, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });

    return response.data;
};

export interface FormQuestionResponseDTO {
    QuestionResponseId?: number;
    FormResponseId?: number;
    FormQuestionId: number;
    Response?: string;
}

export const updateFormQuestionResponse = async (id: number, data: FormQuestionResponseDTO): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
        throw new Error('Authorization token not found');
    }

    await axios.put(`https://${apiURL}/FormQuestionResponse/${id}`, data, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
        },
    });
};
